import { PlusIcon } from '@heroicons/react/24/outline';
import { useActorRef, useSelector } from '@xstate/react';
import type { ColDef } from 'ag-grid-community';
import type { CustomCellRendererProps } from 'ag-grid-react';
import { useEffect } from 'react';
import type { SnapshotFrom } from 'xstate';
import { UserContext } from '~/app';
import {
  formatInvitationStatus,
  InvitationActionLinks,
  InvitationStatusPill,
} from '~/components/agGrid/columns/invitationColumns';
import { BasicGrid } from '~/components/agGrid/Grid';
import { Button } from '~/components/buttons/Button/Button';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import '~/components/pageHeaders.css';
import { ACTIONS_COLUMN_BASE } from '~/components/Table/gridUtil';
import { useProviderAssociationRef } from '~/features/market/marketSelectors';
import { MarketContentPanel, MarketPanelHeader } from '~/features/shared/market-content';
import { dateStringFormatter } from '~/features/utils';
import { InviteColleagueModal } from './InviteColleagueModal';
import { colleagueInviteTeamMachine } from './team.machine';

type TeamSnapshot = SnapshotFrom<typeof colleagueInviteTeamMachine>;

const selectInvitations = (state: TeamSnapshot) => state.context.invitations;
const selectLoading = (state: TeamSnapshot) => state.context.loading;
const selectModalOpen = (state: TeamSnapshot) => state.context.modalOpen;
const selectInviteEmail = (state: TeamSnapshot) => state.context.inviteEmail;
const selectInviteValidationError = (state: TeamSnapshot) => state.context.inviteValidationError;
const selectAlert = (state: TeamSnapshot) => state.context.alert;
const selectIsSending = (state: TeamSnapshot) => state.matches('$_SENDING_INVITE');
const selectIsPendingExists = (state: TeamSnapshot) =>
  state.matches('$_PENDING_EXISTS') || state.matches('$_RESENDING_PENDING');
const selectIsResendingPending = (state: TeamSnapshot) => state.matches('$_RESENDING_PENDING');
const selectIsMutatingInvitation = (state: TeamSnapshot) =>
  state.matches('$_RESENDING_INVITE') || state.matches('$_REVOKING_INVITE');

// Exported for ActionsColumnPinning tests — the Actions column below spreads
// ACTIONS_COLUMN_BASE (components/Table/gridUtil.tsx).
export const createInvitationColumns = (
  onRevoke: (id: number) => void,
  onResend: (id: number) => void,
  disabled = false
): ColDef[] => [
  {
    field: 'invitationEmail',
    headerName: 'Email',
  },
  {
    field: 'invitedByName',
    headerName: 'Invited By',
    valueFormatter: ({ value }) => value || '-',
  },
  {
    field: 'invitedAt',
    headerName: 'Invited',
    valueFormatter: ({ value }) => dateStringFormatter(value, 'SHORT_DATE'),
  },
  {
    field: 'expiresAt',
    headerName: 'Expires',
    valueFormatter: ({ value }) => dateStringFormatter(value, 'SHORT_DATE'),
  },
  {
    field: 'status',
    headerName: 'Status',
    // [MCV-1872] The default text filter reads the raw value ('EMAIL_FAILED'),
    // so filter on the label the pill shows ('Email Failed') instead.
    filterValueGetter: ({ data }) => formatInvitationStatus(data?.status),
    cellRenderer: ({ data }: CustomCellRendererProps) => (
      <InvitationStatusPill status={data.status} />
    ),
  },
  {
    // [MCV-1405] Pinning, locking and [MCV-1750] resizable:false come from ACTIONS_COLUMN_BASE.
    ...ACTIONS_COLUMN_BASE,
    width: 170,
    cellRenderer: ({ data }: CustomCellRendererProps) => {
      const canResend =
        data.status === 'PENDING' || data.status === 'EXPIRED' || data.status === 'EMAIL_FAILED';
      const canRevoke = data.status === 'PENDING';

      return (
        <InvitationActionLinks
          onResend={canResend ? () => onResend(data.id) : undefined}
          onRevoke={canRevoke ? () => onRevoke(data.id) : undefined}
          disabled={disabled}
          resendCyTag="employer-colleague-invites-resend"
          revokeCyTag="employer-colleague-invites-revoke"
        />
      );
    },
  },
];

export const TeamPage = () => {
  const { loading: employerLoading, employerUser } = useProviderAssociationRef();
  const userRef = UserContext.useActorRef();
  const teamRef = useActorRef(colleagueInviteTeamMachine);
  const invitations = useSelector(teamRef, selectInvitations);
  const loading = useSelector(teamRef, selectLoading);
  const modalOpen = useSelector(teamRef, selectModalOpen);
  const inviteEmail = useSelector(teamRef, selectInviteEmail);
  const inviteValidationError = useSelector(teamRef, selectInviteValidationError);
  const alert = useSelector(teamRef, selectAlert);
  const sending = useSelector(teamRef, selectIsSending);
  const pendingExists = useSelector(teamRef, selectIsPendingExists);
  const resendingPending = useSelector(teamRef, selectIsResendingPending);
  const mutatingInvitation = useSelector(teamRef, selectIsMutatingInvitation);
  const employerId = employerUser?.employerId;

  useEffect(() => {
    if (!employerId) return;
    teamRef.send({ type: 'ON_LOAD', employerId });
  }, [employerId, teamRef]);

  useEffect(() => {
    if (!alert) return;
    userRef.send({
      type: 'ON_SHOW_ALERT',
      payload: {
        text: alert.text,
        type: alert.type,
        timeout: 4000,
      },
    });
    teamRef.send({ type: 'ON_CLEAR_ALERT' });
  }, [alert, teamRef, userRef]);

  if (employerLoading || !employerUser) {
    return <LoadingSpinner />;
  }

  const handleRevoke = (invitationId: number) => {
    userRef.send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Revoke Invitation',
        message: 'Are you sure you want to revoke this colleague invitation?',
        confirmLabel: 'Revoke',
        onConfirm: () => teamRef.send({ type: 'ON_REVOKE_INVITATION', invitationId }),
      },
    });
  };

  const handleResend = (invitationId: number) => {
    userRef.send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Resend Invitation',
        message:
          'Are you sure you want to resend this colleague invitation? The previous link will stop working and the expiration date will be refreshed.',
        confirmLabel: 'Resend',
        onConfirm: () => teamRef.send({ type: 'ON_RESEND_INVITATION', invitationId }),
      },
    });
  };

  const inviteButton = (
    <Button
      cyTag="employer-colleague-invites-open"
      type="button"
      colorVariant="primary"
      disabled={mutatingInvitation}
      onClick={() => teamRef.send({ type: 'ON_OPEN_MODAL' })}
    >
      <span className="inline-flex items-center gap-2">
        <PlusIcon className="h-4 w-4" />
        Invite Colleague
      </span>
    </Button>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-cy="employer-team">
      <MarketContentPanel
        bodyClassName="p-0"
        header={
          <MarketPanelHeader
            className="mobile-page-header"
            title="Team"
            subtitle="Invite colleagues from your organization and manage pending invitations."
            actions={inviteButton}
          />
        }
      >
        <BasicGrid
          data={invitations}
          loading={loading}
          colDefs={createInvitationColumns(handleRevoke, handleResend, mutatingInvitation)}
          pagination={true}
          cyTag="employer-colleague-invites-grid"
        />
      </MarketContentPanel>

      <InviteColleagueModal
        open={modalOpen}
        sending={sending}
        email={inviteEmail}
        validationError={inviteValidationError}
        pendingExists={pendingExists}
        resendingPending={resendingPending}
        onClose={() => teamRef.send({ type: 'ON_CLOSE_MODAL' })}
        onEmailChange={(email) => teamRef.send({ type: 'ON_SET_INVITE_EMAIL', email })}
        onSend={() => teamRef.send({ type: 'ON_SEND_INVITATION' })}
        onResendPending={() => teamRef.send({ type: 'ON_RESEND_PENDING' })}
        onCancelPending={() => teamRef.send({ type: 'ON_CANCEL_PENDING' })}
      />
    </div>
  );
};
